import { useMemo } from 'react';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { MusicService } from 'fym-shared/src/music-services/types';
import { useGetAccountQuery, useGetCatalogQuery } from '../../../redux/api/fymAPI';
import { usePresaveCampaign } from '../presave/usePresaveCampaign';
import { removeHttpAndSlash } from '../utils/removeHttpRegex';
import { coverImageUrl, hasCoverArt, linkedMusicServices, missingMajorServices } from './publishedPageFacts';

export interface PublishedPage {
  /** A page with a pre-save block: the release is still ahead of it. */
  isPresave: boolean;
  /** The release the page is about, not the internal page name, when we know it. */
  title: string;
  /** ISO 8601 — the pre-save campaign's date, or the catalog's for a release page. Null when unknown. */
  releaseDate: string | null;
  shareUrl: string | null;
  /** The published page in a frame, for the thumbnail. */
  previewUrl: string | null;
  coverUrl: string | null;
  hasCover: boolean;
  /** The services the page routes to, in page order. */
  linkedServices: MusicService[];
  /** Big services the page does not carry. Empty on a pre-save page. */
  missingServices: MusicService[];
  /** Where the bio link lives, for the row that offers to add this page to it. */
  bioLinkHost: string | null;
  mainSmartLinkId: number | null;
  /** True when the published page *is* the bio link, which cannot be added to itself. */
  isBioLink: boolean;
}

/**
 * Everything the published sheet says about a page, read from what the app
 * already holds.
 *
 * Each fact is optional on purpose: a row the data cannot support is dropped by
 * the sheet rather than filled with a guess.
 */
export function usePublishedPage(accountId: number, smartLink: SerializedSmartLink): PublishedPage {
  const { campaign, hasPresaveBlock } = usePresaveCampaign({ accountId, smartLink });
  const { data: accountData } = useGetAccountQuery({ accountId });
  // The catalog knows the release day of a page built from a claimed release.
  // A pre-save page carries its own campaign date, so it does not need this.
  const { data: catalogData } = useGetCatalogQuery(
    { accountId },
    { skip: hasPresaveBlock || smartLink.isMainSmartLink }
  );

  const version = smartLink.publishedSmartLinkVersion ?? smartLink.recentSmartLinkVersion;
  const linkedServices = useMemo(() => linkedMusicServices(version), [version]);
  const missingServices = useMemo(
    () => (hasPresaveBlock ? [] : missingMajorServices(linkedServices)),
    [hasPresaveBlock, linkedServices]
  );

  const catalogReleaseDate = useMemo(() => {
    const item = catalogData?.items.find((catalogItem) => catalogItem.smartLinkId === smartLink.id);
    return item?.releaseDate ?? null;
  }, [catalogData?.items, smartLink.id]);

  const account = accountData?.account;
  const bioLinkHost =
    account?.shareUrl !== null && account?.shareUrl !== undefined ? removeHttpAndSlash(account.shareUrl) : null;

  return {
    isPresave: hasPresaveBlock,
    title: campaign?.trackTitle ?? version.name,
    releaseDate: campaign?.releaseDate ?? catalogReleaseDate,
    shareUrl: smartLink.shareUrl,
    previewUrl: smartLink.shareUrl ?? smartLink.recentSmartLinkVersion.previewUrl,
    coverUrl: coverImageUrl(version),
    hasCover: hasCoverArt(version),
    linkedServices,
    missingServices,
    bioLinkHost,
    mainSmartLinkId: account?.mainSmartLinkId ?? null,
    isBioLink: smartLink.isMainSmartLink,
  };
}
